import {
  Button,
  Checkbox,
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import type { ComponentProps } from "react";
import { useTranslation } from "react-i18next";

import { PERSONA_STAGE_TOOLBAR_CLASS } from "../../../../lib/persona-opportunity-stage-chrome.js";
import type { PersonaScriptsListFilters } from "../../../../lib/persona-scripts-list-search.js";

export interface PersonaScriptsToolbarProps {
  scriptCount: number;
  briefCount: number;
  angleCount: number;
  allExpanded: boolean;
  onToggleExpandAll: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  filters: PersonaScriptsListFilters;
  sentStatusReady?: boolean;
  onFilterChange: <K extends keyof PersonaScriptsListFilters>(
    key: K,
    value: PersonaScriptsListFilters[K],
  ) => void;
  allSelected: boolean;
  someSelected: boolean;
  onToggleSelectAll: () => void;
  canAddScript: boolean;
  shouldHideBriefs?: boolean;
  onAddScript: () => void;
}

const FINE_POINTER_MIN_H =
  "[@media(hover:hover)_and_(pointer:fine)]:min-h-9 sm:[@media(hover:hover)_and_(pointer:fine)]:min-h-8";
const FILTER_CHIP_CLASS =
  `flex h-9 min-h-11 items-center gap-1.5 rounded-sm border px-3 text-xs font-medium sm:h-8 ${FINE_POINTER_MIN_H}`;
const FILTER_SELECT_CLASS =
  `h-9 min-h-11 w-full text-xs transition-colors sm:h-8 sm:w-[160px] ${FINE_POINTER_MIN_H}`;
const TOOLBAR_SEARCH_CLASS =
  `h-9 min-h-11 sm:h-8 ${FINE_POINTER_MIN_H}`;
const TOOLBAR_AI_BUTTON_CLASS =
  `h-9 min-h-11 border border-ai sm:h-8 ${FINE_POINTER_MIN_H}`;

/** Checkbox-style filter chip: pressed narrows the list, pressed again clears. Copied from the Opportunities Scripts toolbar — do not import that feature (KTD15). */
function FilterToggle({
  active,
  icon,
  activeIconClass,
  label,
  tooltip,
  onToggle,
}: {
  active: boolean;
  icon: ComponentProps<typeof Icon>["name"];
  activeIconClass?: string;
  label: string;
  tooltip?: string;
  onToggle: () => void;
}) {
  const button = (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className={cn(
        FILTER_CHIP_CLASS,
        "transition-colors duration-150",
        active
          ? "border-primary/40 bg-primary/5 text-primary"
          : "border-border/60 bg-background text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      <Icon
        name={icon}
        size="xs"
        color="inherit"
        className={cn(
          "transition-colors duration-150",
          active ? cn("text-primary", activeIconClass) : "text-muted-foreground",
        )}
      />
      {label}
    </button>
  );

  if (!tooltip) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

function FilterSelect({
  value,
  onValueChange,
  placeholder,
  options,
  tooltip,
  disabled,
  idleValue = "all",
}: {
  value: string;
  onValueChange: (value: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
  tooltip?: string;
  disabled?: boolean;
  idleValue?: string;
}) {
  const isActive = value !== idleValue;

  const trigger = (
    <SelectTrigger
      aria-label={placeholder}
      className={cn(
        FILTER_SELECT_CLASS,
        isActive && "border-primary/40 bg-primary/5 text-foreground",
      )}
    >
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
  );

  const labeledTrigger = tooltip ? (
    <Tooltip>
      <TooltipTrigger asChild>
        {disabled ? <span className="inline-flex w-full sm:w-auto">{trigger}</span> : trigger}
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  ) : (
    trigger
  );

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      {labeledTrigger}
      <SelectContent>
        <SelectItem value={idleValue}>{placeholder}</SelectItem>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function PersonaScriptsToolbar({
  scriptCount,
  briefCount,
  angleCount,
  allExpanded,
  onToggleExpandAll,
  search,
  onSearchChange,
  filters,
  sentStatusReady = true,
  onFilterChange,
  allSelected,
  someSelected,
  onToggleSelectAll,
  canAddScript,
  shouldHideBriefs = false,
  onAddScript,
}: PersonaScriptsToolbarProps) {
  const { t } = useTranslation();
  const scriptsLabel = t("personaOpportunities.scripts.summaryScripts", {
    defaultValue_one: "{{count}} script",
    defaultValue_other: "{{count}} scripts",
    count: scriptCount,
  });
  const briefsLabel = t("personaOpportunities.scripts.summaryBriefs", {
    defaultValue_one: "{{count}} brief",
    defaultValue_other: "{{count}} briefs",
    count: briefCount,
  });
  const selectAllLabel =
    scriptCount > 0 && briefCount > 0
      ? t("personaOpportunities.scripts.selectAllMixed", {
          defaultValue: "Select all {{scripts}} and {{briefs}}",
          scripts: scriptsLabel,
          briefs: briefsLabel,
        })
      : briefCount > 0
        ? t("personaOpportunities.scripts.selectAllBriefs", {
            defaultValue_one: "Select all {{count}} brief",
            defaultValue_other: "Select all {{count}} briefs",
            count: briefCount,
          })
        : t("personaOpportunities.scripts.selectAll", {
            defaultValue_one: "Select all {{count}} script",
            defaultValue_other: "Select all {{count}} scripts",
            count: scriptCount,
          });

  return (
    <div className={PERSONA_STAGE_TOOLBAR_CLASS}>
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-2 text-sm text-foreground">
          <Icon name="users" size="sm" color="muted" aria-hidden />
          {briefCount > 0
            ? t("personaOpportunities.scripts.summary", {
                defaultValue: "{{scripts}} · {{briefs}} · {{angles}}",
                scripts: t("personaOpportunities.scripts.summaryScripts", {
                  defaultValue_one: "{{count}} script",
                  defaultValue_other: "{{count}} scripts",
                  count: scriptCount,
                }),
                briefs: t("personaOpportunities.scripts.summaryBriefs", {
                  defaultValue_one: "{{count}} brief",
                  defaultValue_other: "{{count}} briefs",
                  count: briefCount,
                }),
                angles: t("personaOpportunities.scripts.summaryAngles", {
                  defaultValue_one: "{{count}} angle",
                  defaultValue_other: "{{count}} angles",
                  count: angleCount,
                }),
              })
            : t("personaOpportunities.scripts.summaryWithoutBriefs", {
                defaultValue: "{{scripts}} · {{angles}}",
                scripts: t("personaOpportunities.scripts.summaryScripts", {
                  defaultValue_one: "{{count}} script",
                  defaultValue_other: "{{count}} scripts",
                  count: scriptCount,
                }),
                angles: t("personaOpportunities.scripts.summaryAngles", {
                  defaultValue_one: "{{count}} angle",
                  defaultValue_other: "{{count}} angles",
                  count: angleCount,
                }),
              })}
        </span>
        {angleCount > 0 ? (
          <button
            type="button"
            onClick={onToggleExpandAll}
            className="inline-flex min-h-11 items-center text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [@media(hover:hover)_and_(pointer:fine)]:min-h-0"
          >
            {allExpanded
              ? t("personaOpportunities.active.collapseAll", "Collapse all")
              : t("personaOpportunities.active.expandAll", "Expand all")}
          </button>
        ) : null}
        {scriptCount > 0 || briefCount > 0 ? (
          <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 text-xs font-medium text-foreground [@media(hover:hover)_and_(pointer:fine)]:min-h-0">
            <Checkbox
              checked={allSelected ? true : someSelected ? "indeterminate" : false}
              onCheckedChange={onToggleSelectAll}
              aria-label={selectAllLabel}
            />
            {selectAllLabel}
          </label>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-40 flex-1 sm:w-52 sm:flex-none">
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={t(
              shouldHideBriefs
                ? "personaOpportunities.scripts.searchPlaceholderScripts"
                : "personaOpportunities.scripts.searchPlaceholder",
              shouldHideBriefs ? "Search scripts" : "Search scripts and briefs",
            )}
            className={cn(TOOLBAR_SEARCH_CLASS, "w-full", search && "pr-8")}
            aria-label={t(
              shouldHideBriefs
                ? "personaOpportunities.scripts.searchPlaceholderScripts"
                : "personaOpportunities.scripts.searchPlaceholder",
              shouldHideBriefs ? "Search scripts" : "Search scripts and briefs",
            )}
          />
          {search ? (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={t("common.clear", "Clear")}
            >
              <Icon name="x" size="xs" color="inherit" aria-hidden />
            </button>
          ) : null}
        </div>
        <TooltipProvider delayDuration={400}>
          {shouldHideBriefs ? null : (
          <FilterSelect
            value={filters.documentKind}
            idleValue="both"
            onValueChange={(value) => {
              if (value === "both" || value === "scripts" || value === "briefs") {
                onFilterChange("documentKind", value);
              }
            }}
            placeholder={t(
              "personaOpportunities.scripts.toolbar.documentKindBoth",
              "Scripts and briefs",
            )}
            options={[
              {
                value: "scripts",
                label: t("personaOpportunities.scripts.toolbar.documentKindScripts", "Scripts"),
              },
              {
                value: "briefs",
                label: t("personaOpportunities.scripts.toolbar.documentKindBriefs", "Briefs"),
              },
            ]}
          />
          )}
          <FilterSelect
            value={filters.platform}
            idleValue="all"
            onValueChange={(value) => {
              if (value === "all" || value === "meta" || value === "shorts" || value === "in_stream") {
                onFilterChange("platform", value);
              }
            }}
            placeholder={t("personaOpportunities.scripts.toolbar.allPlatforms", "All platforms")}
            options={[
              {
                value: "meta",
                label: t("personaOpportunities.scripts.platform.meta", "Meta"),
              },
              {
                value: "shorts",
                label: t("personaOpportunities.scripts.platform.shorts", "YT Shorts"),
              },
              {
                value: "in_stream",
                label: t("personaOpportunities.scripts.platform.in_stream", "YT In-Stream"),
              },
            ]}
          />
          <FilterToggle
            active={filters.favoritesOnly}
            icon="star"
            activeIconClass="fill-primary"
            label={t("personaOpportunities.scripts.toolbar.viewFavorites", "Favorites")}
            onToggle={() => onFilterChange("favoritesOnly", !filters.favoritesOnly)}
          />
          <FilterToggle
            active={filters.hasSketch === "yes"}
            icon="clapperboard"
            label={t("personaOpportunities.scripts.toolbar.hasSketch", "Sketch")}
            tooltip={t(
              "personaOpportunities.scripts.toolbar.hasSketchTooltip",
              "Only scripts with a sketch storyboard",
            )}
            onToggle={() =>
              onFilterChange("hasSketch", filters.hasSketch === "yes" ? "all" : "yes")
            }
          />
          <FilterToggle
            active={filters.hasAnimatic === "yes"}
            icon="play"
            label={t("personaOpportunities.scripts.toolbar.hasAnimatic", "Animatic")}
            tooltip={t(
              "personaOpportunities.scripts.toolbar.hasAnimaticTooltip",
              "Only scripts with an animatic",
            )}
            onToggle={() =>
              onFilterChange("hasAnimatic", filters.hasAnimatic === "yes" ? "all" : "yes")
            }
          />
          <FilterSelect
            value={filters.sentStatus}
            onValueChange={(value) => {
              if (value === "all" || value === "sent" || value === "notSent") {
                onFilterChange("sentStatus", value);
              }
            }}
            placeholder={t(
              "personaOpportunities.scripts.toolbar.anySendStatus",
              "Any send status",
            )}
            options={[
              {
                value: "sent",
                label: t(
                  "personaOpportunities.scripts.toolbar.sendStatusSent",
                  "Sent to production partner",
                ),
              },
              {
                value: "notSent",
                label: t(
                  "personaOpportunities.scripts.toolbar.sendStatusNotSent",
                  "Not sent",
                ),
              },
            ]}
            disabled={!sentStatusReady}
            tooltip={
              sentStatusReady
                ? t(
                    "personaOpportunities.scripts.toolbar.sendStatusTooltip",
                    "Whether the script has been sent to a partner",
                  )
                : t(
                    "personaOpportunities.scripts.toolbar.sendStatusUnavailableTooltip",
                    "Send status is unavailable until partner data loads",
                  )
            }
          />
        </TooltipProvider>
        <Button
          type="button"
          variant="ai"
          size="sm"
          iconStart="sparkles"
          className={TOOLBAR_AI_BUTTON_CLASS}
          disabled={!canAddScript}
          onClick={onAddScript}
        >
          {t("personaOpportunities.scripts.addYourScript", "Add your script")}
        </Button>
      </div>
    </div>
  );
}
